import React, { useState, useEffect, useCallback } from 'react';
import './StudentDissertation.css';
import collegeConfig from '../../config/collegeConfig';
import { saveFile } from '../../utils/saveFile';
import CertUploadWidget from '../shared/CertUploadWidget';
import {
  FaArrowLeft, FaInfoCircle, FaEdit, FaSave, FaTimes,
  FaChevronDown, FaChevronUp, FaCheckCircle, FaGraduationCap, FaSpinner,
  FaDownload, FaTrash, FaFilePdf, FaFileImage
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

// Fixed 6-month buckets used by dissertations submitted before the flexible
// progress-report system existed. Nothing creates new entries in this format
// anymore — it's kept only to display/grade whatever old data already exists.
const PERIODS_LEGACY = ['0-6', '6-12', '12-18', '18-24', '24-30', '30-36'];
const PERIOD_LABELS_LEGACY = {
  '0-6': 'Month 0–6',
  '6-12': 'Month 6–12',
  '12-18': 'Month 12–18',
  '18-24': 'Month 18–24',
  '24-30': 'Month 24–30',
  '30-36': 'Month 30–36'
};

const GRADE_LABELS = { 4: 'Excellent', 3: 'Good', 2: 'Satisfactory', 1: 'Needs Improvement', 0: 'Unsatisfactory' };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatFlexReport = (r) => `${(r.months || []).map(m => MONTH_NAMES[m - 1]).join(', ')} ${r.year}`;
const yearOptions = (() => {
  const y = new Date().getFullYear();
  const arr = [];
  for (let i = y - 2; i <= y + 5; i++) arr.push(i);
  return arr;
})();

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtInput = (d) => {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
};

const guideLabel = (t) => {
  const name = `${t.profile?.firstName || ''} ${t.profile?.lastName || ''}`.trim() || 'Teacher';
  return t.thesisSignedTillDate ? `${name} (thesis signed till ${fmt(t.thesisSignedTillDate)})` : name;
};

export default function StudentDissertation({ onNavigate }) {
  const [dissertation, setDissertation] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [thesisFacultyMode, setThesisFacultyMode] = useState('student_selects');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const [form, setForm] = useState({
    topicName: '',
    pgTeacherIds: [],
    approvalDate: '',
    submissionDate: ''
  });

  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [reportEditing, setReportEditing] = useState(null);
  const [reportDraft, setReportDraft] = useState('');
  const [reportSaving, setReportSaving] = useState(false);

  const [iecUploading, setIecUploading] = useState(false);
  const [iecDownloading, setIecDownloading] = useState(false);
  const [iecDeleting, setIecDeleting] = useState(false);

  const [docLabel, setDocLabel] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const [docBusyId, setDocBusyId] = useState(null);

  const [flexYear, setFlexYear] = useState(new Date().getFullYear());
  const [flexMonths, setFlexMonths] = useState([]);
  const [flexContent, setFlexContent] = useState('');
  const [flexEditingId, setFlexEditingId] = useState(null);
  const [flexSubmitting, setFlexSubmitting] = useState(false);
  const [flexBusyId, setFlexBusyId] = useState(null);

  const token = () => localStorage.getItem('token');

  const PERIODS = PERIODS_LEGACY;
  const PERIOD_LABELS = PERIOD_LABELS_LEGACY;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dissRes, teachRes, settingsRes] = await Promise.all([
        fetch(`${API}/dissertation/my`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/dissertation/department-teachers`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/system-settings`)
      ]);
      const [dissData, teachData, settingsData] = await Promise.all([dissRes.json(), teachRes.json(), settingsRes.json()]);

      if (settingsData.success) setThesisFacultyMode(settingsData.thesisFacultyAssignmentMode || 'student_selects');

      if (dissData.success) {
        setDissertation(dissData.dissertation);
        if (dissData.dissertation) {
          const d = dissData.dissertation;
          const ids = (d.pgTeacherIds || []).map(t => (typeof t === 'object' ? String(t._id || t.id) : String(t)));
          setForm({
            topicName: d.topicName || '',
            pgTeacherIds: ids,
            approvalDate: fmtInput(d.approvalDate),
            submissionDate: fmtInput(d.submissionDate)
          });
        } else {
          setEditMode(true);
        }
      }
      if (teachData.success) setTeachers(teachData.teachers || []);
    } catch {
      setError('Failed to load data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleTeacher = (id) => {
    setForm(f => ({
      ...f,
      pgTeacherIds: f.pgTeacherIds.includes(String(id))
        ? f.pgTeacherIds.filter(x => x !== String(id))
        : [...f.pgTeacherIds, String(id)]
    }));
  };

  const handleSaveDetails = async () => {
    setError(''); setSuccess('');
    if (!form.topicName.trim()) { setError('Please enter the topic name.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/dissertation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        setEditMode(false);
        setSuccess('Dissertation details saved successfully.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save.');
      }
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEditReport = (period) => {
    const existing = dissertation?.reports?.find(r => r.period === period);
    setReportDraft(existing?.content || '');
    setReportEditing(period);
  };

  const handleSaveReport = async (period) => {
    setError(''); setSuccess('');
    setReportSaving(true);
    try {
      const res = await fetch(`${API}/dissertation/report`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ period, content: reportDraft })
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        setReportEditing(null);
        setSuccess(`Month ${period} report saved.`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save report.');
      }
    } catch {
      setError('Failed to save report. Please try again.');
    } finally {
      setReportSaving(false);
    }
  };

  const togglePeriod = (period) => {
    setExpandedPeriods(p => ({ ...p, [period]: !p[period] }));
  };

  const uploadIec = async (payload) => {
    setError(''); setSuccess('');
    setIecUploading(true);
    try {
      const res = await fetch(`${API}/dissertation/iec-certificate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        setSuccess('IEC certificate uploaded successfully.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to upload IEC certificate.');
      }
    } catch {
      setError('Failed to upload IEC certificate.');
    } finally {
      setIecUploading(false);
    }
  };

  const downloadIec = async () => {
    setIecDownloading(true);
    try {
      const res = await fetch(`${API}/dissertation/iec-certificate/download`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        await saveFile(data.data, data.mimeType, data.fileName || 'iec-certificate');
      } else {
        setError(data.message || 'Download failed.');
      }
    } catch {
      setError('Download failed.');
    } finally {
      setIecDownloading(false);
    }
  };

  const deleteIec = async () => {
    if (!window.confirm('Delete the uploaded IEC certificate? This cannot be undone.')) return;
    setIecDeleting(true);
    try {
      const res = await fetch(`${API}/dissertation/iec-certificate`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(d => ({ ...d, iecCertificate: undefined }));
      } else {
        setError(data.message || 'Delete failed.');
      }
    } catch {
      setError('Delete failed.');
    } finally {
      setIecDeleting(false);
    }
  };

  const uploadDocument = async (payload) => {
    setError(''); setSuccess('');
    setDocUploading(true);
    try {
      const res = await fetch(`${API}/dissertation/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, label: docLabel })
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        setDocLabel('');
        setSuccess('Document uploaded successfully.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to upload document.');
      }
    } catch {
      setError('Failed to upload document.');
    } finally {
      setDocUploading(false);
    }
  };

  const downloadDocument = async (docId) => {
    setDocBusyId(docId);
    try {
      const res = await fetch(`${API}/dissertation/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        await saveFile(data.data, data.mimeType, data.fileName || 'document');
      } else {
        setError(data.message || 'Download failed.');
      }
    } catch {
      setError('Download failed.');
    } finally {
      setDocBusyId(null);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDocBusyId(docId);
    try {
      const res = await fetch(`${API}/dissertation/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(d => ({ ...d, documents: (d.documents || []).filter(doc => doc._id !== docId) }));
      } else {
        setError(data.message || 'Delete failed.');
      }
    } catch {
      setError('Delete failed.');
    } finally {
      setDocBusyId(null);
    }
  };

  const toggleFlexMonth = (m) => {
    setFlexMonths(prev => {
      if (prev.includes(m)) return prev.filter(x => x !== m);
      if (prev.length >= 6) { showToast('You can select up to 6 months at a time.'); return prev; }
      return [...prev, m].sort((a, b) => a - b);
    });
  };

  const startEditFlex = (report) => {
    setFlexEditingId(report._id);
    setFlexYear(report.year);
    setFlexMonths(report.months);
    setFlexContent(report.content || '');
  };

  const cancelFlexEdit = () => {
    setFlexEditingId(null);
    setFlexYear(new Date().getFullYear());
    setFlexMonths([]);
    setFlexContent('');
  };

  const submitFlexReport = async () => {
    setError(''); setSuccess('');
    if (flexMonths.length < 1) { setError('Select at least one month.'); return; }
    setFlexSubmitting(true);
    try {
      const url = flexEditingId ? `${API}/dissertation/flexible-report/${flexEditingId}` : `${API}/dissertation/flexible-report`;
      const method = flexEditingId ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: flexYear, months: flexMonths, content: flexContent })
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
        cancelFlexEdit();
        setSuccess('Progress report saved.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save report.');
      }
    } catch {
      setError('Failed to save report. Please try again.');
    } finally {
      setFlexSubmitting(false);
    }
  };

  const deleteFlexReport = async (reportId) => {
    if (!window.confirm('Delete this progress report? This cannot be undone.')) return;
    setFlexBusyId(reportId);
    try {
      const res = await fetch(`${API}/dissertation/flexible-report/${reportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        setDissertation(data.dissertation);
      } else {
        setError(data.message || 'Delete failed.');
      }
    } catch {
      setError('Delete failed.');
    } finally {
      setFlexBusyId(null);
    }
  };

  if (loading) return (
    <div className="stdiss-container">
      <div className="stdiss-loading"><div className="stdiss-spinner" /> Loading dissertation...</div>
    </div>
  );

  const getReport = (period) => dissertation?.reports?.find(r => r.period === period);

  const getPeriodStatus = (period) => {
    const r = getReport(period);
    if (!r) return 'not-started';
    if (r.isGraded) return 'graded';
    if (r.content) return 'submitted';
    return 'not-started';
  };

  const gradedCount = PERIODS.filter(p => {
    const r = getReport(p);
    return r?.isGraded;
  }).length;

  const flexGradedCount = (dissertation?.flexibleReports || []).filter(r => r.isGraded).length;
  const totalGradedCount = gradedCount + flexGradedCount;

  return (
    <div className="stdiss-container">
      {toast && <div className="stdiss-toast">{toast}</div>}

      {/* Header */}
      <div className="stdiss-header">
        <button className="stdiss-back-btn" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft />
        </button>
        <h1 className="stdiss-page-title">
          <FaGraduationCap style={{ marginRight: 8, color: '#667eea' }} />
          Thesis / Dissertation
        </h1>
        {dissertation?.isCompleted && (
          <span className="stdiss-status-completed">
            <FaCheckCircle /> Completed
          </span>
        )}
      </div>

      {/* Instructions */}
      <div className="stdiss-instructions">
        <h4><FaInfoCircle /> Instructions</h4>
        <ul>
          <li>Enter your dissertation topic, supervisors, and IEC approval date in the form below, and upload your IEC approval certificate.</li>
          <li>Submit <strong>Progress Reports</strong> for any 1 to 6 months you choose — pick the year and months, then write your report for that period. You decide your own cycle (monthly, quarterly, half-yearly, etc.).</li>
          <li>Once a report is graded by your teacher, you will not be able to edit or delete it.</li>
          <li>Upload any supporting documents or certificates (synopsis, protocol, plagiarism certificate, etc.) in the <strong>Documents & Certificates</strong> section below.</li>
          <li>Your grades and teacher feedback will appear in the <strong>Results</strong> section below.</li>
        </ul>
      </div>

      {error && <div className="stdiss-alert stdiss-alert-error">{error}</div>}
      {success && <div className="stdiss-alert stdiss-alert-success">{success}</div>}

      {/* Main Details Card */}
      <div className="stdiss-card">
        <div className="stdiss-card-title">
          Dissertation Details
          {dissertation && !editMode && (
            <button className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm" onClick={() => setEditMode(true)}>
              <FaEdit /> Edit
            </button>
          )}
        </div>

        {editMode ? (
          <>
            <div className="stdiss-form-group">
              <label className="stdiss-label">1. Name of the Topic of Study *</label>
              <input
                className="stdiss-input"
                placeholder="Enter your dissertation/thesis topic"
                value={form.topicName}
                onChange={e => setForm(f => ({ ...f, topicName: e.target.value }))}
              />
            </div>

            <div className="stdiss-form-group">
              <label className="stdiss-label">2. Name of P.G. Teacher(s)</label>
              {thesisFacultyMode === 'admin_assigns' ? (
                <div className="stdiss-instructions" style={{ marginTop: 0 }}>
                  <p style={{ margin: 0 }}>
                    Your thesis faculty is assigned by the Admin.
                    {dissertation?.pgTeacherIds?.length > 0
                      ? ' Currently assigned: ' + dissertation.pgTeacherIds.map(guideLabel).join(', ')
                      : ' No faculty has been assigned to you yet.'}
                  </p>
                </div>
              ) : teachers.length === 0 ? (
                <p style={{ fontSize: 13, color: '#888', margin: 0 }}>No teachers found for your department.</p>
              ) : (
                <div className="stdiss-teacher-options">
                  {teachers.map(t => {
                    const id = String(t._id || t.id);
                    const name = `${t.profile?.firstName || ''} ${t.profile?.lastName || ''}`.trim();
                    return (
                      <label key={id} className="stdiss-teacher-option">
                        <input
                          type="checkbox"
                          checked={form.pgTeacherIds.includes(id)}
                          onChange={() => toggleTeacher(id)}
                        />
                        {name}{t.profile?.department ? ` — ${t.profile.department}` : ''}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="stdiss-form-group">
              <label className="stdiss-label">3. Date of Approval of Study by Institutional Ethical Committee (IEC)</label>
              <input
                className="stdiss-input"
                type="date"
                value={form.approvalDate}
                onChange={e => setForm(f => ({ ...f, approvalDate: e.target.value }))}
              />
            </div>

            <div className="stdiss-form-group">
              <label className="stdiss-label">5. Date of Submission</label>
              <input
                className="stdiss-input"
                type="date"
                value={form.submissionDate}
                onChange={e => setForm(f => ({ ...f, submissionDate: e.target.value }))}
              />
            </div>

            <div className="stdiss-btn-row">
              <button className="stdiss-btn stdiss-btn-primary" onClick={handleSaveDetails} disabled={saving}>
                {saving ? <><FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> Saving...</> : <><FaSave /> Save Details</>}
              </button>
              {dissertation && (
                <button className="stdiss-btn stdiss-btn-secondary" onClick={() => { setEditMode(false); setError(''); }}>
                  <FaTimes /> Cancel
                </button>
              )}
            </div>
          </>
        ) : dissertation ? (
          <div className="stdiss-detail-grid">
            <div className="stdiss-detail-item full-width">
              <label>Topic of Study</label>
              <p>{dissertation.topicName}</p>
            </div>
            <div className="stdiss-detail-item full-width">
              <label>P.G. Teacher(s)</label>
              <p>
                {dissertation.pgTeacherIds?.length > 0
                  ? dissertation.pgTeacherIds.map(guideLabel).join(', ')
                  : dissertation.pgTeacherNames?.length > 0
                    ? dissertation.pgTeacherNames.join(', ')
                    : '—'}
              </p>
            </div>
            <div className="stdiss-detail-item">
              <label>IEC Approval Date</label>
              <p>{fmt(dissertation.approvalDate)}</p>
            </div>
            <div className="stdiss-detail-item">
              <label>Submission Date</label>
              <p>{fmt(dissertation.submissionDate)}</p>
            </div>
            <div className="stdiss-detail-item">
              <label>Submitted On</label>
              <p>{fmt(dissertation.createdAt)}</p>
            </div>
            <div className="stdiss-detail-item">
              <label>Reports Graded</label>
              <p>{totalGradedCount}</p>
            </div>
          </div>
        ) : (
          <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Fill in the form above to submit your dissertation details.</p>
        )}
      </div>

      {/* IEC Certification */}
      {dissertation && (
        <div className="stdiss-card">
          <div className="stdiss-card-title">IEC Approval Certificate</div>
          <p style={{ fontSize: 13, color: '#666', marginTop: -10, marginBottom: 16 }}>
            Upload the Institutional Ethical Committee (IEC) approval certificate for your study.
          </p>
          {dissertation.iecCertificate?.fileName ? (
            <div className="stdiss-doc-row">
              {dissertation.iecCertificate.mimeType === 'application/pdf'
                ? <FaFilePdf style={{ color: '#dc2626', fontSize: 20, flexShrink: 0 }} />
                : <FaFileImage style={{ color: '#2563eb', fontSize: 20, flexShrink: 0 }} />}
              <span className="stdiss-doc-name">{dissertation.iecCertificate.fileName}</span>
              <button className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm" onClick={downloadIec} disabled={iecDownloading}>
                {iecDownloading ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaDownload />}
              </button>
              <button className="stdiss-btn stdiss-btn-danger stdiss-btn-sm" onClick={deleteIec} disabled={iecDeleting}>
                {iecDeleting ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaTrash />}
              </button>
            </div>
          ) : (
            <CertUploadWidget
              inputId="iec-cert-upload"
              uploading={iecUploading}
              onUpload={uploadIec}
              label="Choose IEC Certificate (JPG / PNG / PDF, max 8 MB)"
            />
          )}
        </div>
      )}

      {/* Documents & Certificates */}
      {dissertation && (
        <div className="stdiss-card">
          <div className="stdiss-card-title">Documents & Certificates</div>
          <p style={{ fontSize: 13, color: '#666', marginTop: -10, marginBottom: 16 }}>
            Upload supporting documents (synopsis, protocol, plagiarism certificate, etc.).
          </p>
          {(dissertation.documents || []).map(doc => (
            <div className="stdiss-doc-row" key={doc._id}>
              {doc.mimeType === 'application/pdf'
                ? <FaFilePdf style={{ color: '#dc2626', fontSize: 20, flexShrink: 0 }} />
                : <FaFileImage style={{ color: '#2563eb', fontSize: 20, flexShrink: 0 }} />}
              <span className="stdiss-doc-name">{doc.label ? `${doc.label} — ` : ''}{doc.fileName}</span>
              <button className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm" onClick={() => downloadDocument(doc._id)} disabled={docBusyId === doc._id}>
                {docBusyId === doc._id ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaDownload />}
              </button>
              <button className="stdiss-btn stdiss-btn-danger stdiss-btn-sm" onClick={() => deleteDocument(doc._id)} disabled={docBusyId === doc._id}>
                {docBusyId === doc._id ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaTrash />}
              </button>
            </div>
          ))}
          <div className="stdiss-form-group" style={{ marginTop: 12 }}>
            <label className="stdiss-label">Document label (e.g. Synopsis, Protocol)</label>
            <input
              className="stdiss-input"
              placeholder="Optional label for the next upload"
              value={docLabel}
              onChange={e => setDocLabel(e.target.value)}
            />
          </div>
          <CertUploadWidget
            inputId="dissertation-doc-upload"
            uploading={docUploading}
            onUpload={uploadDocument}
            label="Choose Document (JPG / PNG / PDF, max 8 MB)"
          />
        </div>
      )}

      {/* Progress Reports (flexible: student picks year + 1-6 months per report) */}
      {dissertation && (
        <div className="stdiss-card">
          <div className="stdiss-card-title">4. Progress Reports</div>
          <p style={{ fontSize: 13, color: '#666', marginTop: -10, marginBottom: 16 }}>
            Submit a progress report for any 1 to 6 months you choose. Graded reports cannot be edited.
          </p>

          {(dissertation.flexibleReports || []).length > 0 && (
            <div className="stdiss-periods-list" style={{ marginBottom: 16 }}>
              {[...dissertation.flexibleReports].sort((a, b) => (a.year - b.year) || (a.months[0] - b.months[0])).map(report => {
                const isExpanded = expandedPeriods[report._id];
                const status = report.isGraded ? 'graded' : report.content ? 'submitted' : 'not-started';
                return (
                  <div key={report._id} className={`stdiss-period-card ${status}`}>
                    <div className={`stdiss-period-header ${status}`} onClick={() => togglePeriod(report._id)}>
                      <span className="stdiss-period-title">{formatFlexReport(report)}</span>
                      <div className="stdiss-period-right">
                        {status === 'graded' && <span className="stdiss-grade-display">{report.grade}/4</span>}
                        <span className={`stdiss-badge stdiss-badge-${status}`}>
                          {status === 'graded' ? 'Graded' : status === 'submitted' ? 'Submitted' : 'Not Started'}
                        </span>
                        {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="stdiss-period-body">
                        {report.content && <p className="stdiss-period-content-text">{report.content}</p>}
                        {report.isGraded ? (
                          <div className="stdiss-grade-info">
                            <strong>{report.grade}/4 — {GRADE_LABELS[report.grade]}</strong>
                            <div>Graded by: {report.gradedByName || 'Teacher'}</div>
                            <div className="stdiss-grade-meta">on {fmt(report.gradedAt)}</div>
                            {report.teacherComments && <div className="stdiss-grade-comment">"{report.teacherComments}"</div>}
                          </div>
                        ) : (
                          <div className="stdiss-btn-row" style={{ marginTop: 10 }}>
                            <button className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm" onClick={() => startEditFlex(report)}>
                              <FaEdit /> Edit
                            </button>
                            <button
                              className="stdiss-btn stdiss-btn-danger stdiss-btn-sm"
                              onClick={() => deleteFlexReport(report._id)}
                              disabled={flexBusyId === report._id}
                            >
                              {flexBusyId === report._id ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaTrash />} Delete
                            </button>
                          </div>
                        )}
                        {report.updatedAt && !report.isGraded && (
                          <p style={{ fontSize: 11, color: '#bbb', marginTop: 8, marginBottom: 0 }}>Last updated: {fmt(report.updatedAt)}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="stdiss-form-group">
            <label className="stdiss-label">{flexEditingId ? 'Edit report — Year' : 'New report — Year'}</label>
            <select className="stdiss-input" value={flexYear} onChange={e => setFlexYear(Number(e.target.value))}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="stdiss-form-group">
            <label className="stdiss-label">Months (select 1 to 6)</label>
            <div className="stdiss-month-grid">
              {MONTH_NAMES.map((name, idx) => {
                const m = idx + 1;
                const checked = flexMonths.includes(m);
                return (
                  <label key={m} className={`stdiss-month-chip ${checked ? 'selected' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleFlexMonth(m)} />
                    {name.slice(0, 3)}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="stdiss-form-group">
            <label className="stdiss-label">Progress Report Content</label>
            <textarea
              className="stdiss-textarea"
              placeholder="Describe your progress for the selected months..."
              value={flexContent}
              onChange={e => setFlexContent(e.target.value)}
            />
          </div>
          <div className="stdiss-btn-row">
            <button className="stdiss-btn stdiss-btn-success stdiss-btn-sm" onClick={submitFlexReport} disabled={flexSubmitting}>
              {flexSubmitting ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaSave />}
              {flexEditingId ? 'Update Report' : 'Submit Report'}
            </button>
            {flexEditingId && (
              <button className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm" onClick={cancelFlexEdit}>
                <FaTimes /> Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Previous Progress Reports (legacy fixed-period format — read-only continuation) */}
      {dissertation && dissertation.reports?.length > 0 && (
        <div className="stdiss-card">
          <div className="stdiss-card-title">Previous Progress Reports</div>
          <p style={{ fontSize: 13, color: '#666', marginTop: -10, marginBottom: 16 }}>
            Reports submitted under the previous fixed-period format. Graded periods cannot be edited.
          </p>
          <div className="stdiss-periods-list">
            {PERIODS.filter(period => getReport(period)).map(period => {
              const status = getPeriodStatus(period);
              const report = getReport(period);
              const isExpanded = expandedPeriods[period];
              const isEditing = reportEditing === period;

              return (
                <div key={period} className={`stdiss-period-card ${status}`}>
                  <div
                    className={`stdiss-period-header ${status}`}
                    onClick={() => togglePeriod(period)}
                  >
                    <span className="stdiss-period-title">{PERIOD_LABELS[period]}</span>
                    <div className="stdiss-period-right">
                      {status === 'graded' && (
                        <span className="stdiss-grade-display">{report.grade}/4</span>
                      )}
                      <span className={`stdiss-badge stdiss-badge-${status.replace('-', '-')}`}>
                        {status === 'graded' ? 'Graded' : status === 'submitted' ? 'Submitted' : 'Not Started'}
                      </span>
                      {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="stdiss-period-body">
                      {status === 'graded' ? (
                        <>
                          {report.content && (
                            <p className="stdiss-period-content-text">{report.content}</p>
                          )}
                          <div className="stdiss-grade-info">
                            <strong>{report.grade}/4 — {GRADE_LABELS[report.grade]}</strong>
                            <div>Graded by: {report.gradedByName || 'Teacher'}</div>
                            <div className="stdiss-grade-meta">on {fmt(report.gradedAt)}</div>
                            {report.teacherComments && (
                              <div className="stdiss-grade-comment">"{report.teacherComments}"</div>
                            )}
                          </div>
                        </>
                      ) : isEditing ? (
                        <>
                          <textarea
                            className="stdiss-textarea"
                            placeholder="Describe your progress for this period..."
                            value={reportDraft}
                            onChange={e => setReportDraft(e.target.value)}
                          />
                          <div className="stdiss-btn-row" style={{ marginTop: 10 }}>
                            <button
                              className="stdiss-btn stdiss-btn-success stdiss-btn-sm"
                              onClick={() => handleSaveReport(period)}
                              disabled={reportSaving}
                            >
                              {reportSaving ? <FaSpinner style={{ animation: 'stdiss-spin 0.8s linear infinite' }} /> : <FaSave />}
                              {reportSaving ? 'Saving...' : 'Save Report'}
                            </button>
                            <button
                              className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm"
                              onClick={() => setReportEditing(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {report?.content ? (
                            <p className="stdiss-period-content-text">{report.content}</p>
                          ) : (
                            <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 12px' }}>No progress report submitted yet.</p>
                          )}
                          <button
                            className="stdiss-btn stdiss-btn-secondary stdiss-btn-sm"
                            onClick={() => startEditReport(period)}
                          >
                            <FaEdit /> {report?.content ? 'Update Report' : 'Add Progress Report'}
                          </button>
                        </>
                      )}
                      {report?.updatedAt && status !== 'graded' && (
                        <p style={{ fontSize: 11, color: '#bbb', marginTop: 8, marginBottom: 0 }}>
                          Last updated: {fmt(report.updatedAt)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Results */}
      {dissertation && totalGradedCount > 0 && (
        <div className="stdiss-card">
          <div className="stdiss-card-title">Results & Grades</div>
          <div className="stdiss-results-grid">
            {PERIODS.filter(p => getReport(p)?.isGraded).map(period => {
              const report = getReport(period);
              return (
                <div key={period} className="stdiss-result-card">
                  <div className="period-label">{PERIOD_LABELS[period]}</div>
                  <div className="grade-big">{report.grade}</div>
                  <div className="grade-max">/ 4</div>
                  <div className="grader-name">{GRADE_LABELS[report.grade]}</div>
                  {report.gradedByName && (
                    <div className="grader-name" style={{ marginTop: 4 }}>
                      by {report.gradedByName}
                    </div>
                  )}
                </div>
              );
            })}
            {(dissertation.flexibleReports || []).filter(r => r.isGraded).map(report => (
              <div key={report._id} className="stdiss-result-card">
                <div className="period-label">{formatFlexReport(report)}</div>
                <div className="grade-big">{report.grade}</div>
                <div className="grade-max">/ 4</div>
                <div className="grader-name">{GRADE_LABELS[report.grade]}</div>
                {report.gradedByName && (
                  <div className="grader-name" style={{ marginTop: 4 }}>
                    by {report.gradedByName}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
